/**
 * Centralized logging utility for the application
 * Provides consistent logging across the application with environment-based controls
 * Features structured logging with timestamps and metadata
 */

import { sanitizeErrorMessage, sanitizeObject } from './securityUtils.js';

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

// Environment variable to control log level
// Values: 'debug', 'info', 'warn', 'error', 'none'
// Default: 'warn' in production, 'debug' in development
const getLogLevel = () => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.VITE_LOG_LEVEL || (isDevelopment || isTest ? 'debug' : 'warn');
  }
  return isDevelopment || isTest ? 'debug' : 'warn';
};

const LOG_LEVEL = getLogLevel();

// Log level hierarchy
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4
};

const shouldLog = level => {
  const currentLevel = LOG_LEVELS[LOG_LEVEL] || LOG_LEVELS.warn;
  const requestedLevel = LOG_LEVELS[level] || LOG_LEVELS.warn;
  return requestedLevel >= currentLevel;
};

class Logger {
  /**
   * Sanitize sensitive data from logs
   * @param {any} data - Data to sanitize
   * @returns {any} Sanitized data
   */
  static sanitizeData(data) {
    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data === 'string') {
      return sanitizeErrorMessage(data);
    }

    if (typeof data === 'object') {
      return sanitizeObject(data);
    }

    return data;
  }

  /**
   * Create structured log entry with timestamp and metadata
   * @param {string} level - Log level
   * @param {string} message - Log message
   * @param {Array} args - Additional arguments (data to log)
   * @param {object} options - Optional configuration { correlationId }
   * @returns {object} Structured log object
   */
  static createLogEntry(level, message, args = [], options = {}) {
    const timestamp = new Date().toISOString();
    const sanitizedArgs = args.map(arg => this.sanitizeData(arg));
    const logEntry = {
      timestamp,
      level,
      message: this.sanitizeData(message),
      ...(options.correlationId && { correlationId: options.correlationId }),
      ...(sanitizedArgs.length > 0 && { data: sanitizedArgs })
    };

    // In development, also include browser info
    if (isDevelopment && typeof window !== 'undefined') {
      logEntry.userAgent = window.navigator?.userAgent;
      logEntry.url = window.location?.href;
    }

    return logEntry;
  }

  /**
   * Check if an object is error-like (has error properties)
   * @param {any} obj - Object to check
   * @returns {boolean} True if object appears to be an error
   */
  static isErrorLike(obj) {
    if (!obj || typeof obj !== 'object') return false;
    // Check for Error instance
    if (obj instanceof Error) return true;
    // Check for error-like properties (Axios errors, custom errors)
    return (
      ('message' in obj && typeof obj.message === 'string') ||
      'error' in obj ||
      ('code' in obj && 'message' in obj) ||
      ('response' in obj && 'status' in obj.response) ||
      'isAxiosError' in obj
    );
  }

  /**
   * Format an error object for readable console output
   * @param {any} error - Error or error-like object
   * @returns {object} Formatted error object
   */
  static formatError(error) {
    // Handle null/undefined
    if (!error) return { message: 'Unknown error (null)' };

    // Handle string errors
    if (typeof error === 'string') return { message: error };

    // Extract common error properties
    const formatted = {
      message: error.message || error.error?.message || 'Unknown error'
    };

    // Add error name/type
    if (error.name) formatted.name = error.name;
    if (error.code) formatted.code = error.code;

    // Handle Axios errors specifically
    if (error.isAxiosError || error.response) {
      formatted.isAxiosError = true;
      if (error.response) {
        formatted.status = error.response.status;
        formatted.statusText = error.response.statusText;
        // Include response data if it's useful
        if (error.response.data) {
          const data = error.response.data;
          if (typeof data === 'string' && data.length < 200) {
            formatted.responseData = data;
          } else if (typeof data === 'object') {
            formatted.responseData = {
              code: data.code,
              message: data.message || data.error,
              ...(data.error?.code && { errorCode: data.error.code })
            };
          }
        }
      }
      if (error.config?.url) formatted.url = error.config.url;
      if (error.config?.method) formatted.method = error.config.method.toUpperCase();
    }

    // Handle nested error objects
    if (error.error && typeof error.error === 'object') {
      formatted.innerError = {
        code: error.error.code,
        message: error.error.message
      };
    }

    // Add stack trace (limited)
    if (error.stack) {
      formatted.stack = error.stack.split('\n').slice(0, 5).join('\n');
    }

    return formatted;
  }

  /**
   * Format log entry for console output
   * @param {object} logEntry - Structured log entry
   * @param {Array} originalArgs - Original arguments for console
   */
  static formatConsoleOutput(logEntry, originalArgs) {
    const prefix = `[${logEntry.level}] ${logEntry.timestamp}`;

    if (originalArgs.length === 0) {
      return [prefix, logEntry.message];
    }

    // Format error objects for better readability
    // Handles Error instances, Axios errors, and error-like objects
    const sanitizedArgs = originalArgs.map(arg => {
      if (this.isErrorLike(arg)) {
        return this.formatError(arg);
      }
      return arg;
    });

    return [prefix, logEntry.message, ...sanitizedArgs];
  }

  static info(message, ...args) {
    if (shouldLog('info')) {
      // Extract options from last arg if it's an object with correlationId
      const options = this._extractOptions(args);
      const logEntry = this.createLogEntry('INFO', message, args, options);
      const consoleArgs = this.formatConsoleOutput(logEntry, args);

      console.info(...consoleArgs);
    }
  }

  static warn(message, ...args) {
    if (shouldLog('warn')) {
      const options = this._extractOptions(args);
      const logEntry = this.createLogEntry('WARN', message, args, options);
      const consoleArgs = this.formatConsoleOutput(logEntry, args);

      console.warn(...consoleArgs);
    }
  }

  static error(message, ...args) {
    if (shouldLog('error')) {
      const options = this._extractOptions(args);
      const logEntry = this.createLogEntry('ERROR', message, args, options);
      const consoleArgs = this.formatConsoleOutput(logEntry, args);

      console.error(...consoleArgs);
    }
  }

  static debug(message, ...args) {
    if (shouldLog('debug')) {
      const options = this._extractOptions(args);
      const logEntry = this.createLogEntry('DEBUG', message, args, options);
      const consoleArgs = this.formatConsoleOutput(logEntry, args);

      console.debug(...consoleArgs);
    }
  }

  /**
   * Extract options from args if last argument has correlationId
   *
   * IMPORTANT: This method MUTATES the input args array by removing the options object
   * via args.pop(). This is intentional to prevent the options object from appearing
   * in the logged data. The mutation is safe because:
   * 1. args is already a local copy from the ...args spread operator
   * 2. The mutation happens before args is used in createLogEntry
   * 3. The caller never sees the mutated array
   *
   * @param {Array} args - Arguments array (WILL BE MUTATED to remove options)
   * @returns {object} Options object containing { correlationId } or empty object
   * @private
   */
  static _extractOptions(args) {
    const lastArg = args[args.length - 1];

    // Check if last arg is an options object (has correlationId and no other unexpected properties)
    if (
      lastArg &&
      typeof lastArg === 'object' &&
      !Array.isArray(lastArg) &&
      'correlationId' in lastArg &&
      Object.keys(lastArg).length === 1
    ) {
      // Remove options from args array (intentional mutation)
      args.pop();
      return lastArg;
    }

    return {};
  }

  /**
   * Get current log level
   */
  static getLogLevel() {
    return LOG_LEVEL;
  }

  /**
   * Check if a specific log level is enabled
   */
  static isLevelEnabled(level) {
    return shouldLog(level);
  }
}

export default Logger;
